import React, { useState, useEffect } from 'react';
import StatusBadge from '../StatusBadge';
import WorkflowStepper from '../WorkflowStepper';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Printer, Loader2 } from 'lucide-react';
import { formatRupiah } from '../../utils/printHelper';
import api from '../../lib/api';

export function PODetailDialog({ open, onOpenChange, po, onPrint }) {
  const [traceData, setTraceData] = useState(null);
  const [isLoadingTrace, setIsLoadingTrace] = useState(false);

  useEffect(() => {
    if (open && po?.id) {
      setIsLoadingTrace(true);
      api.get(`/purchase-orders/${po.id}/trace`)
        .then(res => setTraceData(res.data))
        .catch(err => console.error("Failed to load trace", err))
        .finally(() => setIsLoadingTrace(false));
    } else {
      setTraceData(null);
    }
  }, [open, po?.id]);
  if (!po) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Detail Purchase Order</span>
            <Button size="sm" variant="outline" onClick={() => onPrint(po)}>
              <Printer className="h-4 w-4 mr-2" /> Print PDF
            </Button>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4" id="print-content">
          <WorkflowStepper currentStatus={po.status} />
          <div className="grid grid-cols-2 gap-4">
            <div><p className="text-sm text-slate-600">PO Number</p><p className="font-mono font-semibold">{po.po_number}</p></div>
            <div><p className="text-sm text-slate-600">Tanggal PO</p><p className="font-semibold">{new Date(po.po_date).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}</p></div>
            <div><p className="text-sm text-slate-600">Supplier</p><p className="font-semibold">{po.supplier_name}</p></div>
            <div><p className="text-sm text-slate-600">Email Supplier</p><p className="font-semibold">{po.email_supplier || po.supplier_data?.email || '-'}</p></div>
            <div><p className="text-sm text-slate-600">Ditujukan untuk</p><p className="font-semibold">{po.company_name || '-'}</p></div>
            <div><p className="text-sm text-slate-600">Termin Pembayaran</p><p className="font-semibold">{po.termin_pembayaran ? `${po.termin_pembayaran} Hari` : '-'}</p></div>
            <div><p className="text-sm text-slate-600">Jenis Pengiriman</p><p className="font-semibold capitalize">{po.jenis_pengiriman === 'full' ? 'Full (Sekaligus)' : 'Partial (Bertahap)'}</p></div>
          </div>

          {po.alamat_pengiriman?.length > 0 && (
            <div>
              <p className="text-sm text-slate-600 mb-1">Alamat Pengiriman</p>
              {po.alamat_pengiriman.map((addr, idx) => (
                <p key={idx} className="text-sm bg-slate-50 p-2 rounded mb-1">{addr}</p>
              ))}
            </div>
          )}

          <div>
            <p className="text-sm text-slate-600 mb-2">Items</p>
            <div className="bg-slate-50 rounded-lg p-4 space-y-2">
              {po.items.map((item, index) => (
                <div key={index} className="flex justify-between items-center border-b pb-2 last:border-b-0">
                  <div>
                    <p className="font-semibold">{item.name}</p>
                    <p className="text-sm text-slate-600">
                      {item.quantity} x Rp {formatRupiah(item.unit_price || 0)}
                      {item.delivery_date && ` | Kirim: ${new Date(item.delivery_date).toLocaleDateString('id-ID')}`}
                    </p>
                  </div>
                  <p className="font-mono font-semibold">Rp {formatRupiah((item.quantity || 0) * (item.unit_price || 0))}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg space-y-2">
            <div className="flex justify-between text-sm">
              <span>Subtotal</span>
              <span className="font-mono">Rp {(po.subtotal || po.total_amount).toLocaleString('id-ID')}</span>
            </div>
            {po.pembulatan !== 0 && po.pembulatan != null && (
              <div className="flex justify-between text-sm">
                <span>Pembulatan</span>
                <span className="font-mono">{po.pembulatan >= 0 ? '+' : ''}Rp {(po.pembulatan || 0).toLocaleString('id-ID')}</span>
              </div>
            )}
            {po.has_ppn && (
              <div className="flex justify-between text-sm">
                <span>PPn ({po.ppn_percentage}%)</span>
                <span className="font-mono">Rp {(po.ppn_amount || 0).toLocaleString('id-ID')}</span>
              </div>
            )}
            {(po.biaya_angkut || 0) > 0 && (
              <div className="flex justify-between text-sm">
                <span>Biaya Angkut</span>
                <span className="font-mono">Rp {(po.biaya_angkut || 0).toLocaleString('id-ID')}</span>
              </div>
            )}
            <div className="flex justify-between font-bold text-lg border-t pt-2">
              <span>Total PO</span>
              <span className="font-mono text-blue-600">Rp {(po.total_amount || 0).toLocaleString('id-ID')}</span>
            </div>
          </div>

          {po.has_dp && (
            <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg" data-testid="po-detail-dp-info">
              <p className="text-sm font-semibold text-amber-800 mb-1">Down Payment</p>
              <div className="text-sm space-y-1">
                <p><span className="text-slate-500">No. DP:</span> <span className="font-mono font-bold text-amber-700">{po.dp_number}</span></p>
                <p><span className="text-slate-500">Nilai DP:</span> <span className="font-mono font-bold text-amber-700">Rp {(po.dp_amount || 0).toLocaleString('id-ID')}</span></p>
                <p><span className="text-slate-500">Sisa Invoice:</span> <span className="font-mono font-bold">Rp {((po.total_amount || 0) - (po.dp_amount || 0)).toLocaleString('id-ID')}</span></p>
              </div>
            </div>
          )}

          {po.notes && (
            <div><p className="text-sm text-slate-600">Catatan</p><p className="text-sm bg-slate-50 p-2 rounded">{po.notes}</p></div>
          )}

          {po.status === 'cancelled' && (
            <div className="bg-slate-100 border border-slate-300 p-3 rounded-lg" data-testid="po-detail-cancel-info">
              <p className="text-sm font-semibold text-slate-700">PO Dibatalkan</p>
              <p className="text-sm text-slate-700">Alasan: {po.cancel_reason || '-'}</p>
              {po.cancelled_at && <p className="text-xs text-slate-500">{new Date(po.cancelled_at).toLocaleString('id-ID')} · {po.cancelled_by_name}</p>}
            </div>
          )}
          {po.status === 'reject' && po.alasan_reject && (
            <div className="bg-red-50 border border-red-200 p-3 rounded-lg" data-testid="po-detail-reject-info">
              <p className="text-sm font-semibold text-red-700">Ditolak Gudang</p>
              <p className="text-sm text-red-800">Alasan: {po.alasan_reject}</p>
            </div>
          )}
          {po.status === 'closed' && (
            <div className="bg-slate-800 text-white p-3 rounded-lg" data-testid="po-detail-close-info">
              <p className="text-sm font-semibold">PO Ditutup (sisa tidak dikirim)</p>
              <p className="text-sm">Alasan: {po.close_reason || '-'}</p>
              {po.closed_at && <p className="text-xs text-slate-300">{new Date(po.closed_at).toLocaleString('id-ID')} · {po.closed_by_name}</p>}
              {po.cancelled_items?.length > 0 && (
                <ul className="text-xs mt-2 list-disc pl-4">
                  {po.cancelled_items.map((c, i) => <li key={i}>{c.name}: dipesan {c.ordered_qty}, diterima {c.received_qty}, dibatalkan {c.cancelled_qty}</li>)}
                </ul>
              )}
              {po.original_total != null && <p className="text-xs mt-1 text-slate-300">Nilai PO awal Rp {(po.original_total || 0).toLocaleString('id-ID')} → final Rp {(po.total_amount || 0).toLocaleString('id-ID')}</p>}
            </div>
          )}
          {(po.revised_from_po_number || po.revised_to_po_number) && (
            <div className="bg-sky-50 border border-sky-200 p-2 rounded text-sm text-sky-800" data-testid="po-detail-revision-info">
              {po.revised_from_po_number && <p>Revisi dari PO {po.revised_from_po_number}</p>}
              {po.revised_to_po_number && <p>Sudah direvisi menjadi PO {po.revised_to_po_number}</p>}
            </div>
          )}

          {po.uses_va && (
            <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg" data-testid="po-detail-va-info">
              <p className="text-sm font-semibold text-amber-800 mb-1">Virtual Account</p>
              {po.va_nomor ? (
                <div className="text-sm space-y-1">
                  <p><span className="text-slate-500">Nomor VA:</span> <span className="font-mono font-bold text-amber-700">{po.va_nomor}</span></p>
                  <p><span className="text-slate-500">Nama VA:</span> <span className="font-bold text-amber-700">{po.va_nama}</span></p>
                </div>
              ) : (
                <p className="text-sm text-amber-600">Menggunakan Virtual Account (data VA akan diisi saat submit invoice)</p>
              )}
            </div>
          )}

          <div className="flex justify-between text-sm text-slate-600 pt-2 border-t">
            <span>Dibuat oleh: {po.created_by_name}</span>
            <span>Status: <StatusBadge status={po.status} /></span>
          </div>

          {/* Timeline / Trace */}
          <div className="mt-8 pt-6 border-t border-slate-200">
            <h3 className="font-bold text-lg mb-4 text-slate-800">Jejak Dokumen (Trace)</h3>
            
            {isLoadingTrace ? (
              <div className="flex justify-center items-center py-8">
                <Loader2 className="animate-spin h-6 w-6 text-indigo-600" />
              </div>
            ) : traceData ? (
              <div className="space-y-6">
                
                {/* DP */}
                {traceData.down_payment && (
                  <div className="relative pl-6 border-l-2 border-amber-300">
                    <div className="absolute -left-[9px] top-0 h-4 w-4 rounded-full bg-amber-500 border-2 border-white"></div>
                    <div className="mb-1">
                      <span className="font-semibold text-slate-800">Down Payment</span>
                      <span className="mx-2 text-slate-400">|</span>
                      <span className="font-mono text-sm">{traceData.down_payment.dp_number}</span>
                    </div>
                    <div className="text-sm text-slate-600 mb-2">
                      Nilai: Rp {(traceData.down_payment.amount || 0).toLocaleString('id-ID')}
                    </div>
                    <StatusBadge status={traceData.down_payment.status} />
                  </div>
                )}
                
                {/* Invoices */}
                {traceData.invoices?.length > 0 ? (
                  <div className="relative pl-6 border-l-2 border-indigo-300">
                    <div className="absolute -left-[9px] top-0 h-4 w-4 rounded-full bg-indigo-500 border-2 border-white"></div>
                    <div className="mb-3 font-semibold text-slate-800">Invoices</div>
                    <div className="space-y-3">
                      {traceData.invoices.map((inv, i) => (
                        <div key={i} className="bg-slate-50 border rounded p-3">
                          <div className="flex justify-between mb-2">
                            <span className="font-mono text-sm font-semibold text-indigo-700">{inv.invoice_number}</span>
                            <span className="font-mono text-sm">Rp {(inv.total_amount || 0).toLocaleString('id-ID')}</span>
                          </div>
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-xs text-slate-500">{inv.created_at ? new Date(inv.created_at).toLocaleString('id-ID') : ''}</span>
                            <StatusBadge status={inv.status} />
                          </div>
                          <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-slate-600">
                            {inv.submitted_from_po_at && (
                              <div><span className="text-slate-400">Submit dari PO:</span> {new Date(inv.submitted_from_po_at).toLocaleString('id-ID')}{inv.submitted_from_po_by_name ? ` oleh ${inv.submitted_from_po_by_name}` : ''}</div>
                            )}
                            {inv.dokumen_dikirim_at && (
                              <div><span className="text-slate-400">Dokumen dikirim:</span> {new Date(inv.dokumen_dikirim_at).toLocaleString('id-ID')}</div>
                            )}
                            {inv.dokumen_diterima_at && (
                              <div><span className="text-slate-400">Dokumen diterima AP:</span> {new Date(inv.dokumen_diterima_at).toLocaleString('id-ID')}</div>
                            )}
                            {inv.submitted_to_payment_at && (
                              <div><span className="text-slate-400">Ke Payment Request:</span> {new Date(inv.submitted_to_payment_at).toLocaleString('id-ID')}{inv.submitted_to_payment_by_name ? ` oleh ${inv.submitted_to_payment_by_name}` : ''}</div>
                            )}
                            {inv.paid_at && (
                              <div className="text-emerald-700"><span className="text-slate-400">Terbayar:</span> {new Date(inv.paid_at).toLocaleString('id-ID')}{inv.paid_by_name ? ` oleh ${inv.paid_by_name}` : ''}</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="relative pl-6 border-l-2 border-slate-200">
                    <div className="absolute -left-[9px] top-0 h-4 w-4 rounded-full bg-slate-300 border-2 border-white"></div>
                    <div className="text-sm text-slate-500 italic">Belum ada invoice diterbitkan</div>
                  </div>
                )}
                
                {/* Bank Process */}
                {traceData.bank_processes?.length > 0 ? (
                  <div className="relative pl-6 border-l-2 border-green-300">
                    <div className="absolute -left-[9px] top-0 h-4 w-4 rounded-full bg-green-500 border-2 border-white"></div>
                    <div className="mb-3 font-semibold text-slate-800">Proses Bank</div>
                    <div className="space-y-3">
                      {traceData.bank_processes.map((bp, i) => (
                        <div key={i} className="bg-slate-50 border rounded p-3">
                          <div className="flex justify-between mb-1">
                            <span className="text-sm font-semibold">{bp.bank_type}</span>
                            <StatusBadge status={bp.status} />
                          </div>
                          <div className="text-sm text-slate-600 mb-2">
                            Dari: {bp.tipe_request} <span className="font-mono text-xs ml-1 bg-slate-200 px-1 rounded">{bp.request_number}</span>
                            {bp.nominal_transaksi != null && (
                              <span className="ml-2 font-mono">Rp {(bp.nominal_transaksi || 0).toLocaleString('id-ID')}</span>
                            )}
                          </div>
                          {bp.export_filename && (
                            <div className="text-xs">
                              <span className="text-slate-500">File:</span> <span className="font-mono">{bp.export_filename}</span>
                              {bp.export_row_no != null && <span className="ml-1">— baris #{bp.export_row_no}</span>}
                            </div>
                          )}
                          {bp.export_batch_id && (
                            <div className="text-xs">
                              <span className="text-slate-500">Batch:</span> <span className="font-mono">{bp.export_batch_label || bp.export_batch_id}</span>
                            </div>
                          )}
                          {bp.exported_at && (
                            <div className="text-xs mt-1">
                              <span className="text-slate-500">Diexport:</span> <span>{new Date(bp.exported_at).toLocaleString('id-ID')}</span>
                              {bp.exported_by_name && <span> oleh {bp.exported_by_name}</span>}
                            </div>
                          )}
                          {bp.uploaded_at && (
                            <div className="text-xs mt-1 text-emerald-700">
                              <span className="text-slate-500">Bukti diupload:</span> <span>{new Date(bp.uploaded_at).toLocaleString('id-ID')}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="relative pl-6 border-l-2 border-transparent">
                    <div className="absolute -left-[9px] top-0 h-4 w-4 rounded-full bg-slate-200 border-2 border-white"></div>
                    <div className="text-sm text-slate-500 italic">Belum masuk antrian bank</div>
                  </div>
                )}
                
              </div>
            ) : (
              <p className="text-sm text-slate-500 italic">Data jejak tidak tersedia.</p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


